import React, { createContext, useCallback, useEffect, useState } from 'react';
import type { OrderItem } from '../types/apiTypes';
import { useDailyScanSessionReset } from '../hooks/useDailyScanSessionReset';
import {
  SCAN_SESSION_CLEARED_EVENT,
  loadScannedItems as loadScannedItemsFromCache,
  removeScanSession,
  unscanOrderItems,
  writeScannedItemIds,
} from '../utils/scanSessionCache';

interface OrderContextValue {
  orderItems: OrderItem[];
  setOrderItems: React.Dispatch<React.SetStateAction<OrderItem[]>>;
  selectedOrderId: string | number | null;
  setSelectedOrderId: React.Dispatch<React.SetStateAction<string | number | null>>;
  shipToName: string;
  setShipToName: React.Dispatch<React.SetStateAction<string>>;
  orderNumberCin7: string;
  setOrderNumberCin7: React.Dispatch<React.SetStateAction<string>>;
  title: string;
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  saveScannedItems: (orderId: string | number, items: OrderItem[]) => void;
  loadScannedItems: (orderId: string | number, items: OrderItem[]) => OrderItem[];
  clearOrderSession: (orderId: string | number) => void;
}

export const OrderContext = createContext<OrderContextValue | null>(null);

export function OrderProvider({ children }: { children: React.ReactNode }) {
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | number | null>(null);
  const [shipToName, setShipToName] = useState('');
  const [orderNumberCin7, setOrderNumberCin7] = useState('');
  const [title, setTitle] = useState('');

  useEffect(() => {
    const onCleared = (): void => {
      setOrderItems((prev) => {
        if (!prev.some((item) => item.scanned)) {
          return prev;
        }
        return unscanOrderItems(prev);
      });
    };
    window.addEventListener(SCAN_SESSION_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(SCAN_SESSION_CLEARED_EVENT, onCleared);
  }, []);

  useDailyScanSessionReset();

  const saveScannedItems = useCallback((orderId: string | number, items: OrderItem[]): void => {
    writeScannedItemIds(orderId, items);
  }, []);

  const loadScannedItems = useCallback((orderId: string | number, items: OrderItem[]): OrderItem[] => {
    return loadScannedItemsFromCache(orderId, items);
  }, []);

  const clearOrderSession = useCallback((orderId: string | number): void => {
    removeScanSession(orderId);
  }, []);

  return (
    <OrderContext.Provider
      value={{
        orderItems,
        setOrderItems,
        selectedOrderId,
        setSelectedOrderId,
        shipToName,
        setShipToName,
        orderNumberCin7,
        setOrderNumberCin7,
        title,
        setTitle,
        saveScannedItems,
        loadScannedItems,
        clearOrderSession
      }}
    >
      {children}
    </OrderContext.Provider>
  );
}
